import { useState, type ReactNode } from "react";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";
import { api, EDITABLE, type EditableField, type Me, type ProfilePatch } from "./api";

const inputClass =
  "h-11 w-full rounded-md border border-ground-edge bg-ground px-3.5 text-sm text-chrome placeholder:text-chrome-dim transition-colors focus:border-gold/60 focus:outline-none";

const BIO_MAX = 500;

type Draft = Pick<Me, EditableField>;

function draftOf(me: Me): Draft {
  const d = {} as Record<EditableField, unknown>;
  for (const k of EDITABLE) d[k] = me[k];
  return d as Draft;
}

// changes is the patch of fields that differ from what the server sent.
export function changes(me: Me, draft: Draft): ProfilePatch {
  const patch: Record<string, unknown> = {};
  for (const k of EDITABLE) {
    const before = me[k];
    const after = draft[k];
    const same = Array.isArray(before) && Array.isArray(after)
      ? before.length === after.length && before.every((v, i) => v === after[i])
      : before === after;
    if (!same) patch[k] = after;
  }
  return patch as ProfilePatch;
}

function Label({ children, icon, htmlFor }: { children: ReactNode; icon: IconName; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
      <Icon name={icon} className="text-sm text-gold" />
      {children}
    </label>
  );
}

function FieldError({ field, error }: { field: string; error: { field?: string; message: string } | null }) {
  if (!error || error.field !== field) return null;
  return (
    <p role="alert" data-testid={`error-${field}`} className="mt-2 text-[11px] font-bold text-alert">
      {error.message}
    </p>
  );
}

function memberSince(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

export default function ProfileTab({ me, onSaved }: { me: Me; onSaved: (me: Me) => void }) {
  const t = copy.dashboard.profile;
  const [draft, setDraft] = useState<Draft>(() => draftOf(me));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ field?: string; message: string } | null>(null);
  const [notice, setNotice] = useState("");

  const set = <K extends EditableField>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setNotice("");
    if (error?.field === key) setError(null);
  };

  const toggleGenre = (genre: string) =>
    set("genres", draft.genres.includes(genre) ? draft.genres.filter((g) => g !== genre) : [...draft.genres, genre]);

  const save = async () => {
    const patch = changes(me, draft);
    if (Object.keys(patch).length === 0) {
      setNotice(t.nothing);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice("");
    const res = await api.updateMe(patch);
    setBusy(false);
    if (res.ok) {
      onSaved(res.data);
      setDraft(draftOf(res.data));
      setNotice(t.saved);
      return;
    }
    setError({ field: res.field, message: res.error });
  };

  const text = (key: "city" | "state" | "country" | "instagram" | "soundcloud" | "twitter" | "cash_app", label: string) => (
    <div>
      <label htmlFor={`me-${key}`} className="mb-2 block text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
        {label}
      </label>
      <input id={`me-${key}`} value={draft[key]} onChange={(e) => set(key, e.target.value)} className={inputClass} />
      <FieldError field={key} error={error} />
    </div>
  );

  return (
    <div className="rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-7">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
        <div className="lg:col-span-2">
          <Label icon="user" htmlFor="me-username">{t.username}</Label>
          <input id="me-username" value={draft.username} onChange={(e) => set("username", e.target.value)} className={inputClass} />
          <FieldError field="username" error={error} />
          <p className="mt-2 text-[11px] text-chrome-dim">{t.usernameHelp}</p>
        </div>

        <div className="lg:col-span-2">
          <Label icon="global" htmlFor="me-website">{t.website}</Label>
          <input
            id="me-website"
            type="url"
            value={draft.website}
            onChange={(e) => set("website", e.target.value)}
            placeholder={t.websitePlaceholder}
            className={inputClass}
          />
          <FieldError field="website" error={error} />
        </div>

        <div className="lg:col-span-2">
          <Label icon="quillPen" htmlFor="me-bio">{t.bio}</Label>
          <textarea
            id="me-bio"
            value={draft.bio}
            maxLength={BIO_MAX}
            onChange={(e) => set("bio", e.target.value)}
            rows={3}
            placeholder={t.bioPlaceholder}
            className="w-full resize-none rounded-md border border-ground-edge bg-ground px-3.5 py-3 text-sm text-chrome placeholder:text-chrome-dim transition-colors focus:border-gold/60 focus:outline-none"
          />
          <p className="bf-tabular mt-1.5 text-right text-[11px] text-chrome-dim">
            {draft.bio.length}/{BIO_MAX}
          </p>
          <FieldError field="bio" error={error} />
        </div>

        <div>
          <Label icon="userSettings">{t.role}</Label>
          <div className="inline-flex rounded-full border border-ground-edge bg-ground p-1">
            {t.roles.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => set("role", option.value)}
                aria-pressed={draft.role === option.value}
                className={`bf-focusable cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                  draft.role === option.value ? "bg-money text-ground" : "text-chrome-dim hover:text-chrome"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <FieldError field="role" error={error} />
        </div>

        <div>
          <Label icon="notification">{t.notify}</Label>
          <button
            type="button"
            role="switch"
            aria-checked={draft.notify_1v1}
            onClick={() => set("notify_1v1", !draft.notify_1v1)}
            className="bf-focusable flex cursor-pointer items-center gap-3"
          >
            <span className={`relative flex h-6 w-11 items-center rounded-full transition-colors ${draft.notify_1v1 ? "bg-money" : "bg-ground-edge"}`}>
              <span
                className={`absolute h-4 w-4 rounded-full bg-ground transition-transform ${draft.notify_1v1 ? "translate-x-6" : "translate-x-1"}`}
              />
            </span>
            <span className="text-sm text-chrome">{t.notifyLabel}</span>
          </button>
        </div>

        <div>
          <Label icon="moneyDollar" htmlFor="me-budget">{t.budget}</Label>
          <div className="relative">
            <select
              id="me-budget"
              value={draft.budget_1v1}
              onChange={(e) => set("budget_1v1", Number(e.target.value))}
              className={`${inputClass} cursor-pointer appearance-none pr-9`}
            >
              {me.budget_options.map((b) => (
                <option key={b} value={b} className="bg-ground text-chrome">
                  ${b}
                </option>
              ))}
            </select>
            <Icon name="arrowDown" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-chrome-dim" />
          </div>
          <FieldError field="budget_1v1" error={error} />
        </div>

        <div className="lg:col-span-2">
          <Label icon="equalizer">{t.genres}</Label>
          <div className="flex flex-wrap gap-2">
            {me.genre_options.map((genre) => {
              const checked = draft.genres.includes(genre);
              return (
                <button
                  key={genre}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  onClick={() => toggleGenre(genre)}
                  className={`bf-focusable flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                    checked ? "border-money/50 bg-money/10 text-money" : "border-ground-edge text-chrome-dim hover:text-chrome"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded-[4px] border transition-colors ${
                      checked ? "border-money bg-money text-ground" : "border-ground-edge"
                    }`}
                  >
                    {checked && <Icon name="check" className="text-[11px]" />}
                  </span>
                  {genre}
                </button>
              );
            })}
          </div>
          <FieldError field="genres" error={error} />
        </div>

        <div className="lg:col-span-2 border-t border-ground-edge pt-6">
          <Label icon="mapPin">{t.location}</Label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {text("city", t.city)}
            {text("state", t.state)}
            {text("country", t.country)}
          </div>
        </div>

        <div className="lg:col-span-2">
          <Label icon="links">{t.links}</Label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {text("instagram", t.instagram)}
            {text("soundcloud", t.soundcloud)}
            {text("twitter", t.twitter)}
            {text("cash_app", t.cashApp)}
          </div>
        </div>

        <div className="lg:col-span-2">
          <Label icon="paypal" htmlFor="me-paypal_email">{t.paypal}</Label>
          <input
            id="me-paypal_email"
            type="email"
            value={draft.paypal_email}
            onChange={(e) => set("paypal_email", e.target.value)}
            className={inputClass}
          />
          <FieldError field="paypal_email" error={error} />
          <p className="mt-2 text-[11px] text-chrome-dim">{t.paypalHelp}</p>
        </div>
      </div>

      {error && !error.field && (
        <p role="alert" className="mt-6 rounded-md border border-alert/40 bg-alert/10 px-4 py-3 text-sm text-chrome">
          {error.message}
        </p>
      )}

      <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-ground-edge pt-5">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="bf-focusable h-11 cursor-pointer whitespace-nowrap rounded-md bg-money px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:opacity-60"
        >
          {busy ? t.saving : t.save}
        </button>
        {notice && (
          <span role="status" className="bf-fade-in flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-money">
            <Icon name="checkboxCircle" className="text-base" />
            {notice}
          </span>
        )}
        {me.member_since && (
          <span className="ml-auto text-[11px] uppercase tracking-[0.14em] text-chrome-dim">{t.memberSince(memberSince(me.member_since))}</span>
        )}
      </div>
    </div>
  );
}
